import { LocationAnchorSource, useAccommodationsConsoleStateInitializationQuery } from '@graphql/generated';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getIsValidLatitudeAndLongitude } from '../../AccommodationsConsole.utils';
import { AccommodationsConsoleState, SortOption } from '../../AccommodationsConsole.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ChangedLocation } from '../../components/Map/Map.types';
import { DEFAULT_SEARCH_RADIUS } from './constants';
import { deriveRoomsQueryParams, deriveSearchDateQueryParams } from './utils';
import { useDeriveLocationQueryParams } from './useDeriveLocationQueryParams';
import { StaysSearchTerms } from '@apps/stays/types';
import { formatDateComponentsToString } from '@shared/utils/dateHelpers';
import { useTranslation } from '@shared/core';
import { addAction, addError } from '@shared/utils/logger';
import { useValidatedAccommodationsConsoleSearchParams } from '../useAccommodationsConsoleSearchParamsHelper';
import { useApplyUserInitiatedSearchParamsToUrl } from './useApplyUserInitiatedSearchParamsToUrl';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { withWindow } from '@shared/utils/withWindow';
import { useListingsSortQueryParam } from './useListingsSortQueryParam';

export type UseAccommodationsConsoleSearchParamsStateReturn = ReturnType<typeof useAccommodationsConsoleSearchParamsState>;

export const useAccommodationsConsoleSearchParamsState = (args: { eventHandle: Maybe<string> }) => {
  const { eventHandle } = args;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasUserInitiatedCustomSearch, setHasUserInitiatedCustomSearch] = useState(false);
  const config = useAccommodationsConsoleConfig();
  const { currentSort } = useListingsSortQueryParam();

  const shouldSkipBestLocationAnchorQuery = !eventHandle;
  const { t } = useTranslation('accommodationsConsole');

  const { data, loading, called } = useAccommodationsConsoleStateInitializationQuery({
    variables: {
      name: eventHandle || '',
    },
    skip: shouldSkipBestLocationAnchorQuery,
    fetchPolicy: 'cache-first',
    onCompleted: data => {
      if (!data?.eventByName?.bestLocationAnchor) {
        addAction('Best anchor location null (AccommodationsConsole)', {
          context: 'bestLocationAnchor',
          eventHandle,
        });
      }
    },
    onError: error =>
      addError(error, {
        source: 'Accommodations console state initialization failed (AccommodationsConsole)',
        context: 'eventByName',
        errorMessage: error.message,
        eventHandle,
      }),
    batchMode: 'fast',
  });

  const staysSearchParams = useValidatedAccommodationsConsoleSearchParams();

  const bestLocationAnchor = data?.eventByName?.bestLocationAnchor;
  const deriveLocationParams = useDeriveLocationQueryParams(bestLocationAnchor, loading, called);

  const { applyUserInitiatedSearchParamsToUrl } = useApplyUserInitiatedSearchParamsToUrl();

  /**
   * This ref is used to track if the state has been initialized.
   * on first mount, start from defaults -- otherwise, read from query params.
   */
  const isInitializedRef = useRef(false);

  const shouldProcessState = !shouldSkipBestLocationAnchorQuery || data || (!loading && !data);

  const checkIfShouldInitializeStateWithDefaults = useEventCallback(() => {
    return !isInitializedRef.current;
  });

  const concludeStateInitialization = useEventCallback(() => {
    isInitializedRef.current = true;
  });

  const state = useMemo<AccommodationsConsoleState | undefined>(() => {
    if (shouldProcessState) {
      /**
       * Reset the accommodation console to the default state --
       * the idea is that users would share the accommodation link, not the search link.
       */
      const shouldUseDefaults = checkIfShouldInitializeStateWithDefaults();
      // const params = new URLSearchParams(location.search);

      // Set the startDate + endDate
      const datesQp = deriveSearchDateQueryParams({
        dateSearchParams: staysSearchParams,
        eventFinalizedDateString: data?.eventByName?.info?.finalizedDate?.dateString,
        useDefaults: shouldUseDefaults,
      });

      // Set occupancy
      const roomsQp = deriveRoomsQueryParams({ roomsSearchParams: staysSearchParams, useDefaults: shouldUseDefaults });

      // Set Location
      const locationQp = deriveLocationParams({ locationSearchParams: staysSearchParams, useDefaults: shouldUseDefaults });

      /**
       * If the best location anchor is a venue or location, then use the venue location.
       * Calculate venue distance from the venue location.
       * Venue should stick to the same even after search for different location.
       */
      const isVenueLocationAnchor = bestLocationAnchor && [LocationAnchorSource.event_venue, LocationAnchorSource.event_location].includes(bestLocationAnchor.source);
      const venue = {
        name: bestLocationAnchor?.friendlyName || '',
        location: isVenueLocationAnchor
          ? (() => {
              const lat = parseFloat(bestLocationAnchor.latitude);
              const lng = parseFloat(bestLocationAnchor.longitude);
              return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
            })()
          : null,
      };

      const searchRadiusQp = staysSearchParams.searchRadius.value;

      const updatedState: AccommodationsConsoleState = {
        circularRegionSearch:
          locationQp && getIsValidLatitudeAndLongitude(locationQp.lat, locationQp.lng)
            ? {
                latitude: locationQp.lat,
                longitude: locationQp.lng,
                radius: typeof searchRadiusQp === 'number' ? searchRadiusQp : DEFAULT_SEARCH_RADIUS,
                googlePlaceId: locationQp.googlePlaceId,
              }
            : null,
        searchLocation: locationQp?.destination,
        searchDates: {
          checkin: datesQp.checkin,
          checkout: datesQp.checkout,
        },
        occupancies: {
          unitCount: roomsQp.rooms.length,
          occupancies: roomsQp.rooms,
        },
        venue,
      };

      return updatedState;
    }

    return undefined;
  }, [checkIfShouldInitializeStateWithDefaults, shouldProcessState, data, staysSearchParams, deriveLocationParams, bestLocationAnchor]);

  // Update URL on client side only (not during SSR) when parameters are missing
  // This prevents React Router SSR from seeing history.replace() calls during server-side rendering
  useEffect(() => {
    // Skip during SSR or if state is not ready
    const isClient = withWindow(() => true, false);
    if (!isClient || !state) {
      return;
    }

    // Only run if we haven't initialized yet
    if (isInitializedRef.current) {
      return;
    }

    const shouldUseDefaults = checkIfShouldInitializeStateWithDefaults();
    const datesQp = deriveSearchDateQueryParams({
      dateSearchParams: staysSearchParams,
      eventFinalizedDateString: data?.eventByName?.info?.finalizedDate?.dateString,
      useDefaults: shouldUseDefaults,
    });

    const roomsQp = deriveRoomsQueryParams({ roomsSearchParams: staysSearchParams, useDefaults: shouldUseDefaults });
    const locationQp = deriveLocationParams({ locationSearchParams: staysSearchParams, useDefaults: shouldUseDefaults });
    const searchRadiusQp = staysSearchParams.searchRadius.value;

    // Only update URL if this is a new location and parameters are missing
    if (
      (datesQp.isMissingInUrl || roomsQp.isMissingInUrl || locationQp?.isMissingInUrl || typeof searchRadiusQp !== 'number') &&
      locationQp &&
      getIsValidLatitudeAndLongitude(locationQp?.lat, locationQp?.lng)
    ) {
      concludeStateInitialization();
      applyUserInitiatedSearchParamsToUrl({
        dates: {
          checkin: datesQp.checkin,
          checkout: datesQp.checkout,
        },
        map: {
          destination: locationQp?.destination,
          lat: locationQp.lat,
          lng: locationQp.lng,
          radius: DEFAULT_SEARCH_RADIUS,
        },
        rooms: roomsQp.rooms,

        actionType: 'replace',
      });
    }
  }, [state, checkIfShouldInitializeStateWithDefaults, concludeStateInitialization, data, staysSearchParams, deriveLocationParams, applyUserInitiatedSearchParamsToUrl]);

  // Track if user has performed a search action (map or form)
  const handleIsUserSearched = useCallback(() => {
    if (!hasUserInitiatedCustomSearch) {
      setHasUserInitiatedCustomSearch(true);
    }
  }, [hasUserInitiatedCustomSearch]);

  // Only disable initial load after user has searched AND state is updated
  // This ensures we don't trigger unnecessary API calls during state transitions
  useEffect(() => {
    if (isInitialLoad && hasUserInitiatedCustomSearch) {
      setIsInitialLoad(false);
    }
  }, [state, hasUserInitiatedCustomSearch, isInitialLoad]);

  /**
   * For when the user clicks the "search this area" button on the map.
   */
  const handleOnChangeLocation = useEventCallback((changedLocation: ChangedLocation) => {
    applyUserInitiatedSearchParamsToUrl({
      map: {
        lat: changedLocation.location.lat,
        lng: changedLocation.location.lng,
        radius: changedLocation.radius,
        destination: t('map', 'areaSearchLocationValue')(),
      },
      pickedByCouple: false,
      // If default sort order is couple pick then change it to top pick after manually search or click on Search this area cta
      sort: currentSort === SortOption.COUPLES_PICKS ? SortOption.TOP_MATCHES : currentSort,
      actionType: 'push',
    });
    handleIsUserSearched();
  });

  const handleHeaderSearchFormSubmit = useEventCallback((value: StaysSearchTerms) => {
    applyUserInitiatedSearchParamsToUrl({
      map: {
        lat: value.lat,
        lng: value.lng,
        destination: value.destination,
        googlePlaceId: value.googlePlaceId,
      },
      dates: {
        checkin: formatDateComponentsToString(value.startDate),
        checkout: formatDateComponentsToString(value.endDate),
      },
      rooms: value.rooms,
      pickedByCouple: false,
      // If default sort order is couple pick then change it to top pick after manually search or click on Search this area cta
      ...(currentSort === SortOption.COUPLES_PICKS && { sort: SortOption.TOP_MATCHES }),
      actionType: 'push',
    });
    handleIsUserSearched();
  });

  return {
    state,
    loading,
    handleOnChangeLocation,
    handleHeaderSearchFormSubmit,
    isInitialLoad,
    isShowingAllCouplesPicks: isInitialLoad && !hasUserInitiatedCustomSearch && config.listingsPanel.listingData.shouldShowAllAccommodations,
  };
};
